import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import React from 'react'
import { BUGSNAG_REDACTED_KEYS, scrubBreadcrumbMetadata, scrubPinText } from './pinScrub'

// Expose Bugsnag on window so the SDK is reachable from the browser
// console -- needed for `Bugsnag.notify(new Error('test'))` smoke tests
// after a deploy. The npm import is module-scoped by default; only the
// CDN script-tag distribution attaches to window automatically. Keep
// the typing explicit so TS knows about window.Bugsnag in any code
// that references it.
declare global {
  interface Window {
    Bugsnag: typeof Bugsnag
  }
}

let bugsnagInitialized = false

export function initBugsnag(): boolean {
  if (bugsnagInitialized) return true

  const apiKey = import.meta.env.VITE_BUGSNAG_API_KEY?.trim()
  if (!apiKey) return false

  Bugsnag.start({
    apiKey,
    plugins: [new BugsnagPluginReact(React)],
    appVersion: import.meta.env.VITE_BUILD_VERSION || undefined,
    releaseStage:
      import.meta.env.VITE_BUGSNAG_RELEASE_STAGE
      || import.meta.env.MODE
      || 'production',
    redactedKeys: BUGSNAG_REDACTED_KEYS,
    onBreadcrumb: (breadcrumb) => {
      try {
        breadcrumb.message = scrubPinText(breadcrumb.message ?? '')
        if (breadcrumb.metadata) scrubBreadcrumbMetadata(breadcrumb.metadata)
      } catch {
        // Can't prove it's clean: don't record it.
        return false
      }
    },
  })

  if (typeof window !== 'undefined') {
    window.Bugsnag = Bugsnag
  }

  bugsnagInitialized = true
  return true
}

// Returns the React ErrorBoundary class if Bugsnag is initialized, else
// null. Caller renders <BugsnagErrorBoundary /> conditionally so the
// app still mounts in dev / tests without an API key.
//
// Return type is inferred from `plugin.createErrorBoundary(React)` (or
// `null`) so it exactly matches Bugsnag's BugsnagErrorBoundary class --
// including the full FallbackComponent prop shape ({error, info,
// clearError}). A hand-rolled narrower type would diverge from the
// plugin's actual signature; the prior attempt declared
// `FallbackComponent: ({error}) =&gt; ...` and the build failed with
// TS2322 because Bugsnag's FallbackComponent receives `info` and
// `clearError` too.
export function getBugsnagErrorBoundary() {
  if (!bugsnagInitialized) return null
  const plugin = Bugsnag.getPlugin('react')
  if (!plugin) return null
  return plugin.createErrorBoundary(React)
}
